import styled from '@emotion/styled';
import {Col, Container, media, Row, Spacer, spacing, Visible} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {Button} from 'UI/components/action/Button';
import {InlineLink} from 'UI/components/action/InlineLink';
import {IntroHeader} from 'UI/components/layout/IntoHeader';
import {PayloadImage} from 'UI/components/layout/PayloadImage';
import {PayloadLexical} from 'UI/components/layout/PayloadLexical';
import {H4} from 'UI/components/layout/Text';
import {ViewportTrigger} from 'UI/components/layout/ViewportTrigger';

import {FadeInAnimation} from 'UI/animations/shared';
import {useTextImage} from 'UI/modules/Blocks/TextImage/useTextImage';

import {transient} from 'Application/configs/emotionTransientProps';
import {Enlarge} from 'Images/icons/Enlarge';
import {Zoom} from 'Images/icons/Zoom';

import type {Media, TextImageButton, TextImageContent, TextImageModuleIntro, TextImageType} from 'types/payload-types';

/**
 * The props for the `TextImage` component, which renders a section combining text, image, and optional CTA button.
 * This interface defines the configuration for the section’s content, introductory header,
 * unique identifiers, and testing attributes. It ensures proper setup for accessibility,
 * navigation, and automated testing.
 */
interface ComponentProps {
    /**
     * The main content of the section, including headline, descriptive text,
     * image data, and layout orientation (e.g., whether the image should appear on the left).
     */
    content: TextImageContent;
    /**
     * Optional configuration for a call-to-action (CTA) button, including label,
     * link details, and visibility rules.
     */
    cta?: TextImageButton;
    /**
     * An optional flag to hide the entire block when set to true.
     * When `true`, the component renders nothing.
     *
     * @default false
     */
    hideBlock?: TextImageType['hideBlock'];
    /**
     * The unique identifier for the hero block, used for linking and accessibility.
     * This ID is essential for in-page navigation and should be unique across the page.
     */
    id: TextImageType['id'];
    /**
     * The intro section configuration, which may include an overline, headline,
     * and supporting descriptive text. If omitted, no intro is rendered.
     */
    intro?: TextImageModuleIntro;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'TextImage'
     */
    testId?: string;
}

/**
 * A content block component that renders a text-and-image layout with optional intro and CTA button.
 * This component displays a section consisting of an introductory header (`IntroHeader`),
 * followed by a responsive two-column layout: one column for textual content (headline, rich text,
 * and optional CTA button) and another for an image. The layout can reverse the order of text
 * and image on larger screens based on the `content.imageLeft` property.
 * It leverages the `useTextImage` hook to resolve CTA link attributes (`href`, `rel`, `target`)
 * and only renders the button if `cta.hasButton` is `true`. Accessibility and responsive design
 * are supported through semantic headings, ARIA attributes, and responsive column settings.
 * The component is wrapped in MobX's `observer` so it re-renders automatically in response
 * to observable state changes.
 *
 * @param props           The component props.
 * @param props.content   The main text and image content configuration, including headline, text, image, and layout orientation.
 * @param props.cta       Optional CTA configuration object, which may include button metadata and visibility flags.
 * @param props.hideBlock Optional flag to hide the entire block when set to true.
 * @param props.id        An optional block identifier applied to the intro headline for anchor linking.
 * @param props.intro     Optional intro section containing overline, headline, and supporting text.
 * @param props.testId    An optional identifier used for testing, applied to the container. Defaults to `'TextImage'`.
 * @returns A JSX element rendering the text-image section with optional intro and CTA button.
 *
 * @example
 * ```tsx
 * <TextImage
 *   id="about-us"
 *   intro={{ headline: 'About Us', overline: 'Welcome', text: introText }}
 *   content={{
 *     headline: 'Our Mission',
 *     text: missionText,
 *     image: missionImage,
 *     imageLeft: true
 *   }}
 *   cta={{ hasButton: true, label: 'Learn More', linkType: 'internal', internal: '/about' }}
 * />
 * ```
 */
const TextImage = observer(({content, cta, hideBlock = false, id, intro, testId = 'TextImage'}: ComponentProps) => {
    const {href, label, Modal, openModal, rel, target} = useTextImage(cta);

    return hideBlock ? null : (
        <Container testId={testId}>
            <IntroHeader blockId={id} intro={intro} />
            <ViewportTrigger
                align="stretch"
                baseComponent={Row}
                hasNoGap="no-row"
                isReverse={content.imageLeft ? ['lg', 'xl', 'xxl'] : []}
            >
                <Col lg={6} xs={12}>
                    <ContentWrapper>
                        {content.headline && <MotionH4 as="h3" variants={FadeInAnimation}>{content.headline}</MotionH4>}
                        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                        {content.text && (
                            <motion.div variants={FadeInAnimation}>
                                <PayloadLexical description={content.text} />
                            </motion.div>
                        )}
                        {cta?.hasButton && (
                            <>
                                <Spacer y={4} isNotStretching />
                                <MotionButton
                                    as="link"
                                    href={href}
                                    rel={rel}
                                    target={target}
                                    variant="secondary"
                                    variants={FadeInAnimation}
                                >
                                    {label}
                                </MotionButton>
                            </>
                        )}
                    </ContentWrapper>
                </Col>
                <Col align="center" justify="center" lg={6} xs={12}>
                    <ImageWrapper variants={FadeInAnimation}>
                        {content.image && (
                            <Image
                                $fillMode={content.imageDimensions}
                                expectedSizes={{
                                    xs: 272, sm: 500, md: 500, lg: 464, xl: 575, xxl: 684
                                }}
                                image={content.image}
                            />
                        )}
                        {content.modalImage && (
                            <>
                                <StyledButton as="button" icon={Enlarge} variant="tertiary" onClick={openModal} />
                                <StyledInlineLink href="https://www.openstreetmap.org/copyright" target="_blank">
                                    © OpenStreetMap contributors
                                </StyledInlineLink>
                            </>
                        )}
                    </ImageWrapper>
                </Col>
            </ViewportTrigger>
            {content.modalImage && (
                <Modal>
                    <ModalImageWrapper>
                        <ModalImage src={(content.image as Media).url ?? ''} />
                        <Visible md sm xs>
                            <ZoomHint hidden={!content.hintText}>
                                <Zoom />
                                <Hint>{content.hintText}</Hint>
                            </ZoomHint>
                        </Visible>
                        <StyledInlineLinkModal href="https://www.openstreetmap.org/copyright" target="_blank">
                            © OpenStreetMap contributors
                        </StyledInlineLinkModal>
                    </ModalImageWrapper>
                </Modal>
            )}
        </Container>
    );
});

TextImage.displayName = 'TextImage';

export {TextImage};

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(6)};
    height: 100%;
    min-height: 30rem;
    padding: 0 0 ${spacing(18)} 0;

    ${media('lg')} {
        padding: ${spacing(20)} ${spacing(14)};
    }
`;

const MotionH4 = styled(motion.create(H4))``;

const MotionButton = styled(motion.create(Button))``;

const ImageWrapper = styled(motion.div)`
    aspect-ratio: 1 / 1;
    border-radius: 24px;
    height: 100%;
    max-width: 50rem;
    overflow: hidden;
    position: relative;
    width: 100%;

    ${media('lg')} {
        aspect-ratio: initial;
        height: 100%;
        max-width: initial;
        width: 100%;
    }
`;

interface ImageProps {
    $fillMode: 'contain' | 'cover';
}

const Image = styled(PayloadImage, transient)<ImageProps>`
    height: 100%;
    max-height: 100%;
    max-width: 100%;
    min-height: 0;
    min-width: 0;
    object-fit: ${({$fillMode}) => $fillMode};
    width: 100%;
`;

const StyledButton = styled(Button)`
    height: fit-content;
    inset-block: 50%;
    inset-inline-end: 0;
    position: absolute;
    transform: translateY(-50%);
`;

const StyledInlineLink = styled(InlineLink)`
    bottom: ${spacing(4)};
    font-family: ${({theme}) => theme.fonts.Poppins};
    font-size: 1.2rem;
    font-weight: 400;
    line-height: 1.4;
    position: absolute;
    right: 0;
    text-decoration: none;
    word-break: break-all;
    word-wrap: break-word;

    &:hover {
        text-decoration: underline;
    }
`;

const ModalImageWrapper = styled.div`
    height: 100%;
    overflow: auto;
    width: 100%;
`;

const ModalImage = styled.img`
    max-width: 100%;

    ${media('md')} {
        max-width: none;
    }
`;

const ZoomHint = styled.div`
    align-items: center;
    bottom: ${spacing(5)};
    display: flex;
    justify-content: center;
    left: ${spacing(4)};
    position: absolute;
`;

const Hint = styled.p`
    color: ${({theme}) => theme.colors.textSecondary};
    font-family: ${({theme}) => theme.fonts.Poppins};
    font-size: 1.4rem;
    font-weight: 400;
    line-height: 1.4;
    word-break: break-all;
    word-wrap: break-word;
`;

const StyledInlineLinkModal = styled(StyledInlineLink)`
    bottom: ${spacing(1)};
    font-size: 1.2rem;
    right: ${spacing(1)};

    ${media('md')} {
        bottom: ${spacing(5)};
        font-size: 1.4rem;
        right: ${spacing(6)};
    }
`;